/* eslint-disable @typescript-eslint/no-misused-promises */
/* eslint-disable prettier/prettier */
import { useEffect, useRef } from 'react';
import { getCOSConfig } from '@/pages/dashboard/services/dashboard';
import COS from 'cos-js-sdk-v5';
import { ICOSConfig } from '@/pages/dashboard/types/dashboard';

// const COS = require('cos-js-sdk-v5');

// 临时token认证信息 有效时长，单位为秒，目前配置是59分钟（1小时少1分钟），后端设置为1小时，前端有效时长比后端设置的少一分钟，防止出现后端超时了，前端还没超时的情况
const DURATION = 1 * 60 * 60 - 1 * 60;

// COS插件异常code，用于插件调用者 根据error code 判断具体是属于什么异常
const COS_ERROR_CODE = 'COS_ERROR';

const createCOSError = (msg: string, error?: any): any => ({
  ...error,
  code: COS_ERROR_CODE,
  message: msg,
});

export default function useCos({ bucket = '', region = '' } = {}) {
  // COS实例
  const cosInstance = useRef<any>(null);
  // COS配置信息，后端返回的
  const cosConfig = useRef<ICOSConfig | null>(null);
  // COS操作公共入参
  const baseParams = useRef<{ bucket: string; region: string }>({
    bucket,
    region,
  });

  const putObject = async (params: any): Promise<any> => {
    if (!cosInstance.current)
      return Promise.reject(createCOSError('cos插件未初始化'));

    return new Promise((resolve, reject) => {
      const cos = cosInstance.current;
      const { bucket, region } = baseParams.current || {};
      const { path, file, onProgress } = params || {};

      cos.putObject(
        {
          Bucket:
            cosConfig.current?.bucket ||
            bucket /* 填入您自己的存储桶，必须字段 */,
          Region:
            cosConfig.current?.region ||
            region /* 存储桶所在地域，例如ap-beijing，必须字段 */,
          Key: path /* 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段 */,
          StorageClass: 'STANDARD',
          Body: file, // 上传文件对象
          onProgress(progressData: any) {
            onProgress instanceof Function && onProgress(progressData);
          },
        },
        (err: any, data: any) => {
          if (err) {
            reject(createCOSError('COS pubObject方法异常', err));
          } else {
            resolve(data);
          }
        },
      );
    });
  };

  const putObjects = async (params: any[]): Promise<any[]> => {
    if (!Array.isArray(params))
      Promise.reject(createCOSError('批量上传参数异常'));
    try {
      const promises = params?.map((item) => putObject(item));
      const results: any[] = await Promise.allSettled(promises);
      if (
        results?.length &&
        results.every((item) => item.status === 'rejected')
      ) {
        throw new Error(results[0].reason);
      }
      return results;
    } catch (e) {
      console.log(e)
    }
  };

  const getObjectUrl = async (params: any): Promise<any> => {
    if (!cosInstance.current)
      return Promise.reject(createCOSError('cos插件未初始化'));

    return new Promise((resolve, reject) => {
      const cos = cosInstance.current;
      const { bucket, region } = baseParams.current || {};
      const { path } = params || {};

      cos.getObjectUrl(
        {
          Bucket:
            cosConfig.current?.bucket ||
            bucket /* 填入您自己的存储桶，必须字段 */,
          Region:
            cosConfig.current?.region ||
            region /* 存储桶所在地域，例如ap-beijing，必须字段 */,
          Key: path /* 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段 */,
          Expires:
            60 *
            60 *
            24 *
            365 *
            5 /* 签名几秒后失效，默认900秒，这里设置成5年有效时间 */,
          Sign: true /* 获取带签名的对象URL，因为url权限是私有的，不带签名获取是拒绝访问的，带签名获取的话签名是有过期时间的 */,
        },
        (err: any, data: any) => {
          if (err) {
            reject(createCOSError('COS getObjectUrl方法异常', err));
            return;
          }
          /* url为对象访问url */
          const url = data.Url;
          /* 复制downloadUrl的值到浏览器打开会自动触发下载 */
          const downloadUrl = `${
            url + (url.indexOf('?') > -1 ? '&' : '?')
          }response-content-disposition=attachment`; // 补充强制下载的参数
          resolve({ url, downloadUrl });
        },
      );
    });
  };

  const getObjectsUrl = async (params: any[]): Promise<any[]> => {
    if (!cosInstance.current)
      return Promise.reject(createCOSError('cos插件未初始化'));

    if (!Array.isArray(params)) throw new Error('批量获取对象访问url参数异常');
    try {
      const promises = params?.map((item) => getObjectUrl(item));
      const results: any[] = await Promise.allSettled(promises);
      if (
        results?.length &&
        results.every((item) => item.status === 'rejected')
      ) {
        throw new Error(results[0].reason);
      }
      return results;
    } catch (e) {
      console.log(e)
    }
  };

  const deleteObject = async (params: any): Promise<any> => {
    if (!cosInstance.current)
      return Promise.reject(createCOSError('cos插件未初始化'));

    return new Promise((resolve, reject) => {
      const cos = cosInstance.current;
      const { bucket, region } = baseParams.current || {};
      const { path } = params;

      cos.deleteObject(
        {
          Bucket:
            cosConfig.current?.bucket ||
            bucket /* 填入您自己的存储桶，必须字段 */,
          Region:
            cosConfig.current?.region ||
            region /* 存储桶所在地域，例如ap-beijing，必须字段 */,
          Key: path /* 存储在桶里的对象键（例如1.jpg，a/b/test.txt），必须字段 */,
        },
        (err: any, data: any) => {
          if (err) {
            reject(createCOSError('COS deleteObject方法异常', err));
            return;
          }
          resolve(data);
        },
      );
    });
  };

  const getBucket = async (params?: any): Promise<any> => {
    if (!cosInstance.current)
      return Promise.reject(createCOSError('cos插件未初始化'));

    return new Promise((resolve, reject) => {
      const cos = cosInstance.current;
      const { bucket, region } = baseParams.current || {};
      const { prefix = '' } = params || {};
      cos.getBucket(
        {
          Bucket:
            cosConfig.current?.bucket ||
            bucket /* 填入您自己的存储桶，必须字段 */,
          Region:
            cosConfig.current?.region ||
            region /* 存储桶所在地域，例如ap-beijing，必须字段 */,
          Prefix: prefix /* Prefix表示列出的object的key以prefix开始，非必须 */,
        },
        (err: any, data: any) => {
          if (err) {
            reject(createCOSError('COS getBucket方法异常', err));
            return;
          }
          resolve(data);
        },
      );
    });
  };

  useEffect(() => {
    (async () => {
      cosConfig.current = await getCOSConfig();
    })();

    cosInstance.current = new COS({
      async getAuthorization(options: any, callback: Function) {
        // 异步获取临时密钥
        // 服务端 JS 和 PHP 例子：https://github.com/tencentyun/cos-js-sdk-v5/blob/master/server/
        // 服务端其他语言参考 COS STS SDK ：https://github.com/tencentyun/qcloud-cos-sts-sdk
        // STS 详细文档指引看：https://cloud.tencent.com/document/product/436/14048

        // const url = 'http://example.com/server/sts.php'; // url替换成您自己的后端服务
        // const xhr = new XMLHttpRequest();
        // xhr.open('GET', url, true);
        // xhr.onload = function (e) {
        //   let data;
        //   let credentials;
        //   try {
        //     data = JSON.parse(e.target.responseText);
        //     credentials = data.credentials;
        //   } catch (e) {
        //   }
        //   if (!data || !credentials) {
        //     return console.error('credentials invalid:\n' + JSON.stringify(data, null, 2));
        //   };
        //   callback({
        //     TmpSecretId: credentials.tmpSecretId,
        //     TmpSecretKey: credentials.tmpSecretKey,
        //     SecurityToken: credentials.sessionToken,
        //     // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
        //     StartTime: data.startTime, // 时间戳，单位秒，如：1580000000
        //     ExpiredTime: data.expiredTime, // 时间戳，单位秒，如：1580000000
        //   });
        // };
        // xhr.send();

        // 请求接口获取图片上传的token等数据
        const res = await getCOSConfig();
        const startTime = Math.floor(Date.now() / 1000);
        const endTime = Math.floor(startTime + DURATION);
        cosConfig.current = res || null;
        callback({
          TmpSecretId: res?.tmp_secret_id || '',
          TmpSecretKey: res?.tmp_secret_key || '',
          SecurityToken: res?.session_token || '',
          // 建议返回服务器时间作为签名的开始时间，避免用户浏览器本地时间偏差过大导致签名错误
          StartTime: startTime, // 时间戳，单位秒，如：1580000000
          ExpiredTime: endTime, // 时间戳，单位秒，如：1580000000
        });
      },
    });
  }, []);

  return {
    cosInstance,
    getBucket,
    putObject,
    putObjects,
    getObjectUrl,
    getObjectsUrl,
    deleteObject,
  };
}
